/**
 * Waits for an element to appear in the DOM and returns it as a Promise.
 * Uses MutationObserver to detect DOM changes and find the element.
 * 
 * @param selector - Can be one of:
 *   - CSS selector string to find element
 *   - Function that returns element
 *   - Element itself
 * @param timeout - Maximum time in ms to wait before rejecting (default 10000)
 * @returns Promise that resolves with the found element, or rejects if timeout reached
 * @throws Error if element not found within timeout period
 * 
 * @example
 * // Wait for element by CSS selector
 * const elem = await waitForElement('#my-element');
 * 
 * // Wait for element using function
 * const elem = await waitForElement(() => document.querySelector('.dynamic-elem'));
 * 
 * // Wait with custom timeout
 * const elem = await waitForElement('#my-element', 5000);
 */

export function waitForElement<T extends HTMLElement = HTMLElement>(
    selector: string | (() => T ),
    timeout: number = 10000
): Promise<T> {
    return new Promise((resolve, reject) => {
        let timeoutId: number;
        let isResolved = false;

        const getElement = (): T | null => {
            if (typeof selector === 'string') {
                return document.querySelector<T>(selector);
            }
            if (typeof selector === 'function') {
                return selector();
            }
            return null;
        };
        
        const element = getElement();
        if (element) {
            resolve(element);
            return;
        }

        const observer = new MutationObserver((mutations, obs) => {

            const element = getElement();
            if (element) {
                isResolved = true;
                obs.disconnect();
                clearTimeout(timeoutId);
                resolve(element);
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        timeoutId = window.setTimeout(() => {
            observer.disconnect();
            if (!isResolved) {
                reject(new Error(`Element not found after ${timeout}ms`));
            }
        }, timeout);
    });
}

/**
 * Gets the viewport size of an element.
 * @param element - The element to measure.
 * @returns The width and height of the element's viewport.
 */
export function getElementViewportSize(element: HTMLElement): { width: number; height: number } {
    if (!element) return { width: 0, height: 0 };

    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const width = Math.max(0, Math.min(rect.width, viewportWidth));
    const height = Math.max(0, Math.min(rect.height, viewportHeight));

    return { width, height };
}
